import clsx from "clsx"
import { Ref, forwardRef } from "react"

import debounce from "@utils/debounce"

import { Skeleton } from "@particles/index"

import { Icon } from "@atoms"

import styles from "./Chip.module.scss"
import { AvatarChipProps, ChipProps, IconChipProps } from "./Chip.types"

function Chip(props: ChipProps, ref: Ref<HTMLButtonElement>) {
    const {
        children,
        variant = "default",
        type = "button",
        className,

        isTabbable = true,

        onClick,
        noDebounce = false,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isLoading = false,
        isSelected = false,

        ...rest
    } = props

    const iconSize = 14

    const icon: IconChipProps["icon"] = (props as IconChipProps)?.icon
    const iconClassName: IconChipProps["iconClassName"] = (props as IconChipProps)?.iconClassName
    const avatar: AvatarChipProps["avatar"] = (props as AvatarChipProps)?.avatar

    return (
        <button
            ref={ref}
            className={clsx(
                styles.base,
                {
                    [styles.isLoading]: isLoading,
                    [styles.isFocused]: _isFocused,
                    [styles.isHovered]: _isHovered,
                    [styles.isPressed]: isPressed,
                    [styles.isSelected]: isSelected,
                },
                className,
            )}
            aria-selected={isSelected}
            type={type}
            onClick={
                noDebounce
                    ? onClick
                    : debounce((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClick?.(event))
            }
            disabled={isDisabled || isLoading}
            aria-disabled={isDisabled}
            aria-busy={isLoading}
            aria-pressed={isPressed}
            tabIndex={isTabbable && !isDisabled ? 0 : -1}
            {...rest}
        >
            {variant === "avatar" && (
                <>{isLoading ? <Skeleton circle={true} height={16} width={16} enableAnimation={true} /> : avatar}</>
            )}
            {variant === "icon" && (
                <>
                    {isLoading ? (
                        <div className={styles.iconSkeleton}>
                            <Skeleton circle={true} height={14} width={14} enableAnimation={true} />
                        </div>
                    ) : (
                        <Icon icon={icon} size={iconSize} container="fixedHeight" className={iconClassName} />
                    )}
                </>
            )}
            <div className={styles.content}>
                {isLoading ? (
                    <div className={styles.contentSkeleton}>
                        <Skeleton height={10} width={40} borderRadius={0} enableAnimation={true} />
                    </div>
                ) : (
                    children
                )}
            </div>
        </button>
    )
}

export default forwardRef(Chip)
